import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { IconDownloadAlt, TooltipTrigger, Tooltip } from '@spotify-internal/encore-creator-web';
import moment from 'moment';

import { updateDateRange } from '../../../store/updateDateRange';
import styles from './index.module.scss';
import { trackEvent } from 'lib/Tracking';
import { Dropdown } from 'vendor/Dropdown';
import { Calendar } from 'vendor/Calendar';
import { selectDateRange, selectDatesAvailable } from 'store/selectors';
import { FormattedMessage } from 'react-intl';

export class DateRangeSelector extends PureComponent {
  static propTypes = {
    dateRange: PropTypes.shape({
      startDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
    updateDateRange: PropTypes.func.isRequired,
    datesAvailable: PropTypes.shape({
      from: PropTypes.string.isRequired,
      until: PropTypes.string.isRequired,
    }),
    downloadDataAsCsv: PropTypes.func.isRequired,
    isDownloadable: PropTypes.bool,
    showFirstDaysOptions: PropTypes.bool,
  };

  static defaultProps = {
    isDownloadable: true,
  };

  state = {
    isExpanded: false,
    isCustomDate: false,
    showCsvTooltip: false,
  };

  componentDidMount() {
    window.addEventListener('mousedown', this.onWindowEvent, true);
  }

  componentWillUnmount() {
    /* istanbul ignore next */
    window.removeEventListener('mousedown', this.onWindowEvent, true);
  }

  onWindowEvent = ({ type, target }) => {
    /* istanbul ignore else */
    if (type === 'mousedown' && !this.element.contains(target)) {
      this.clearState();
    }
  };

  onIsCustomDate = () => this.setState({ isCustomDate: true });

  onDatesChange = ({ startDate, endDate, label }) => {
    this.props.updateDateRange(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'), label);
    trackEvent({ category: 'date', action: 'change', label });
    this.clearState();
  };

  toggleIsExpanded = () => {
    const { isExpanded } = this.state;
    this.clearState();
    this.setState({ isExpanded: !isExpanded });
  };

  downloadCSV = () => {
    trackEvent({ category: 'csv', action: 'download', label: 'episode-streams' });
    this.props.downloadDataAsCsv();
  };

  clearState = () => {
    this.setState({
      isCustomDate: false,
      isExpanded: false,
    });
  };

  render() {
    const { isExpanded, isCustomDate } = this.state;
    const { startDate, endDate } = this.props.dateRange;
    const expandedElement = isCustomDate ? (
      <Calendar
        onSave={this.onDatesChange}
        onCancel={this.clearState}
        datesAvailable={this.props.datesAvailable}
        startDate={moment(startDate)}
        endDate={moment(endDate)}
      />
    ) : (
      <Dropdown
        onDatesChange={this.onDatesChange}
        onIsCustomDate={this.onIsCustomDate}
        endDate={this.props.datesAvailable.until}
        showFirstDaysOptions={this.props.showFirstDaysOptions}
      />
    );

    return (
      <div className={styles.container} data-qa="date-range-selector">
        <div className={styles.dateSelectorContainer}>
          <div
            ref={el => {
              this.element = el;
            }}
            className={styles.parent}
          >
            <button className={styles.button} onClick={this.toggleIsExpanded}>
              {this.props.dateRange.label}
              <svg className={styles.icon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
                <path d="M8 13.77L.62 5.129l.76-.649L8 12.231l6.62-7.751.76.649z" />
              </svg>
            </button>
            {isExpanded && expandedElement}
          </div>
          {/* istanbul ignore next */ this.props.isDownloadable ? (
            <TooltipTrigger
              overlay={
                this.state.showCsvTooltip && (
                  <Tooltip>
                    <FormattedMessage
                      id="date-range-selector.download-csv"
                      description="tooltip for 'download cvs' button"
                      defaultMessage="Download CSV"
                    />
                  </Tooltip>
                )
              }
              placement={TooltipTrigger.top}
              onShow={() => this.setState({ showCsvTooltip: true })}
              onHide={() => this.setState({ showCsvTooltip: false })}
            >
              <div className={styles.csvDownload}>
                <IconDownloadAlt
                  iconSize={32}
                  onClick={this.downloadCSV}
                  alt="Download CSV"
                  data-testid="download"
                />
              </div>
            </TooltipTrigger>
          ) : null}
        </div>
      </div>
    );
  }
}

const selector = createStructuredSelector({
  dateRange: selectDateRange,
  datesAvailable: selectDatesAvailable,
});

const actions = { updateDateRange };

export const ConnectedDateRangeSelector = connect(selector, actions)(DateRangeSelector);
